import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import DataTable from "../components/common/DataTable";
import Button from "../components/common/Button";
import { ACTIONS, queryAuditLogs } from "../services/auditService";
import { getPendingRequestsCount } from "../services/accessRequestService";
import { downloadCsv, formatDateTime } from "../utils/format";
import { ACTION_STYLE_MAP } from "../constants/actions";

export default function AuditLogsPage() {
  const [filters, setFilters] = useState({ search: "", action: "", from: "", to: "" });
  const [logs, setLogs] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function load() {
      const [rows, pending] = await Promise.all([
        queryAuditLogs({ action: filters.action, from: filters.from, to: filters.to }),
        getPendingRequestsCount(),
      ]);
      setLogs(rows);
      setPendingCount(pending);
    }
    load();
  }, [filters.action, filters.from, filters.to]);

  const filtered = useMemo(
    () =>
      logs.filter((l) =>
        `${l.userName || ""} ${l.userEmail || ""}`.toLowerCase().includes(filters.search.toLowerCase())
      ),
    [logs, filters.search]
  );

  const paged = useMemo(() => filtered.slice((page - 1) * 50, page * 50), [filtered, page]);
  const pages = Math.max(1, Math.ceil(filtered.length / 50));

  return (
    <AppShell
      title="Audit Logs"
      pendingCount={pendingCount}
      rightTopContent={
        <Button
          onClick={() =>
            downloadCsv(
              "audit-logs.csv",
              [["Timestamp", "User", "Email", "Action", "Target", "Details"]].concat(
                filtered.map((l) => [formatDateTime(l.timestamp), l.userName, l.userEmail, l.action, l.targetType, l.targetLabel])
              )
            )
          }
        >
          Export CSV
        </Button>
      }
    >
      <Card>
        <div className="control-row" style={{ marginBottom: 12 }}>
          <input
            className="control-input"
            placeholder="Search user name or email"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
          <select className="control-select" value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}>
            <option value="">All actions</option>
            {Object.values(ACTIONS).map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </select>
          <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          <button type="button" className="btn btn-secondary" onClick={() => setFilters({ search: "", action: "", from: "", to: "" })}>
            Clear Filters
          </button>
        </div>
        <DataTable
          columns={[
            { key: "timestamp", label: "Timestamp", render: (l) => formatDateTime(l.timestamp) },
            { key: "userName", label: "User" },
            { key: "userEmail", label: "Email" },
            {
              key: "action",
              label: "Action",
              render: (l) => <span className={`badge ${ACTION_STYLE_MAP[l.action] || "badge-standard"}`}>{l.action}</span>,
            },
            { key: "targetType", label: "Target" },
            { key: "targetLabel", label: "Details" },
          ]}
          rows={paged}
          empty={<p className="muted">No audit logs found.</p>}
        />
        <div className="row-between" style={{ marginTop: 10 }}>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="muted">
            Page {page} / {pages}
          </span>
          <Button variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </Card>
    </AppShell>
  );
}
